import React, { useState } from 'react';
import { MdErrorOutline, MdFileDownload } from 'react-icons/md';
import { downloadFromApi } from '../lib/fileDownload';
import { tsFilename } from '../lib/filename';

/**
 * All-or-nothing import failure panel.
 *
 * The backend answers a bad workbook with HTTP 422 and
 *   { ok:false, code:'IMPORT_VALIDATION_FAILED', total, failed, errors:[…], report_id }
 * and imports NOTHING — not even the rows that were fine. That's the one
 * thing the operator must not misread, so the headline says it outright
 * instead of leaving it to a 3-second toast.
 *
 * Rendered by both AllAssets and AssetRegistration from the `report` that
 * `useAssetImport()` stores.
 */

// A badly mapped file can fail every row of a 20k-row sheet. Rendering all of
// them would lock the tab, so cap what we paint — but never silently: the
// footer states the true total and points at the downloadable workbook, which
// is always complete.
const MAX_RENDERED = 200;

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const ImportErrorReport = ({ report, onClose, onToast }) => {
  const [downloading, setDownloading] = useState(false);
  if (!report) return null;

  const errors = Array.isArray(report.errors) ? report.errors : [];
  const total  = Number(report.total) || 0;
  const failed = Number(report.failed) || errors.length;
  const shown  = errors.slice(0, MAX_RENDERED);
  // Two things can hide rows: our render cap, and the backend trimming the
  // `errors` array itself. Compare against `failed` so both are covered.
  const notShown = Math.max(0, failed - shown.length);

  const handleDownload = async () => {
    if (!report.report_id || downloading) return;
    setDownloading(true);
    try {
      // Authenticated binary download: downloadFromApi goes through the app's
      // axios instance with responseType 'blob', so the JWT header is attached
      // and a JSON error body still gets parsed into a readable message.
      await downloadFromApi(
        `/assets/import-errors/${encodeURIComponent(report.report_id)}`,
        tsFilename('asset-import-errors', 'xlsx'),
        XLSX_MIME,
      );
    } catch (err) {
      if (onToast) onToast(err.message || 'Could not download the error report', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const stickyTh = { position: 'sticky', top: 0, zIndex: 1 };

  return (
    <div className="card" style={{ marginBottom: 12, padding: 0, borderLeft: '4px solid var(--danger)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px' }}>
        <MdErrorOutline style={{ fontSize: 22, color: 'var(--danger)', flexShrink: 0, marginTop: 1 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)' }}>
            Import cancelled — {failed} of {total} row(s) have errors. Nothing was imported.
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
            This import is all-or-nothing: <strong>no assets were added or updated</strong>. Correct the
            rows listed below in your spreadsheet, then upload the file again.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            {report.report_id && (
              <button className="btn btn-danger btn-sm" onClick={handleDownload} disabled={downloading}>
                <MdFileDownload /> {downloading ? 'Preparing…' : 'Download error report (Excel)'}
              </button>
            )}
            <button className="btn btn-outline btn-sm" onClick={onClose}>Dismiss</button>
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Dismiss import errors"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}
        >×</button>
      </div>

      {shown.length > 0 && (
        <div className="table-container" style={{ maxHeight: 340, overflowY: 'auto', borderRadius: 0, borderTop: '1px solid var(--border)' }}>
          <table>
            <thead>
              <tr>
                <th style={{ ...stickyTh, width: 70 }}>Row</th>
                <th style={{ ...stickyTh, width: 190 }}>Column</th>
                <th style={{ ...stickyTh, width: 190 }}>Value</th>
                <th style={stickyTh}>Problem</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((e, idx) => (
                <tr key={`${e?.row ?? '?'}-${e?.column ?? ''}-${idx}`}>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{e?.row ?? '—'}</td>
                  <td>
                    <code style={{ fontSize: 11, color: 'var(--accent)', wordBreak: 'break-word' }}>
                      {e?.column || '—'}
                    </code>
                  </td>
                  <td style={{ wordBreak: 'break-word' }}>
                    {e?.value === '' || e?.value === null || e?.value === undefined
                      ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>(empty)</span>
                      : String(e.value)}
                  </td>
                  <td style={{ color: 'var(--text-secondary)' }}>{e?.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {notShown > 0 && (
        <div style={{
          padding: '10px 16px', background: '#fff3e0', color: '#e65100',
          fontSize: 12, fontWeight: 500, borderTop: '1px solid var(--border)',
        }}>
          Showing {shown.length} of {failed} errors. Download the error report for the complete list.
        </div>
      )}
    </div>
  );
};

export default ImportErrorReport;
